import React, { useEffect, useState } from "react";
import { Loader2, Star, TrendingUp, Eye, MousePointerClick, ShoppingBag, IndianRupee, RefreshCw } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const Stat = ({ icon: Icon, label, value, sub, color = "text-[#1E1B4B]" }) => (
  <div className="bg-white border border-gray-100 rounded-2xl p-4 flex flex-col">
    <div className="flex items-center gap-2 text-[#475569] text-[11px] uppercase tracking-wider font-semibold">
      <Icon size={12} /> {label}
    </div>
    <p className={`text-2xl font-extrabold mt-1.5 ${color}`}>{value}</p>
    {sub && <p className="text-[11px] text-[#94a3b8] mt-0.5">{sub}</p>}
  </div>
);

const EarlyBirdAnalyticsCard = ({ authAxios }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const r = await authAxios.get(`${API_URL}/api/admin/promo/early-bird/analytics?days=30`);
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't load analytics");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (loading && !data) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex items-center justify-center" data-testid="eb-analytics-loading">
        <Loader2 size={20} className="animate-spin text-[#DC283C]" />
      </div>
    );
  }
  if (err) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 text-sm text-rose-700" data-testid="eb-analytics-error">{err}</div>
    );
  }
  if (!data) return null;

  const { funnel, promo, by_page = {}, recent_bookings = [] } = data;
  const pages = Object.entries(by_page).sort((a, b) => (b[1].clicks || 0) - (a[1].clicks || 0));

  return (
    <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden" data-testid="eb-analytics-card">
      <div className="px-5 py-4 bg-gradient-to-r from-amber-500 via-rose-500 to-pink-600 text-white flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
          <Star size={20} />
        </div>
        <div className="flex-1">
          <h3 className="text-base font-bold leading-tight">Early-Bird Conversion Funnel · last 30d</h3>
          <p className="text-[11px] text-white/80">{promo.slots_remaining} of {promo.total_slots} slots left · ₹{promo.discount_inr} OFF · {promo.active ? "active" : "paused"}</p>
        </div>
        <button onClick={load} className="px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 text-[11px] font-bold flex items-center gap-1" data-testid="eb-analytics-refresh">
          <RefreshCw size={11} /> Refresh
        </button>
      </div>

      <div className="p-5 space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Stat icon={Eye} label="Impressions" value={funnel.impressions.toLocaleString("en-IN")} />
          <Stat icon={MousePointerClick} label="Clicks" value={funnel.clicks.toLocaleString("en-IN")} sub={`${funnel.click_through_pct}% CTR`} />
          <Stat icon={ShoppingBag} label="Bookings" value={funnel.bookings.toLocaleString("en-IN")} sub={`${funnel.click_to_booking_pct}% click→book`} color="text-[#DC283C]" />
          <Stat icon={TrendingUp} label="Paid" value={funnel.paid_bookings.toLocaleString("en-IN")} sub={`of ${funnel.bookings} total`} color="text-emerald-600" />
          <Stat icon={IndianRupee} label="Revenue" value={inr(funnel.paid_revenue_inr)} sub="paid only" color="text-emerald-600" />
        </div>

        {pages.length > 0 ? (
          <div className="bg-[#FFFAF5] rounded-2xl p-4">
            <p className="text-[11px] uppercase tracking-wider text-[#475569] font-semibold mb-2">By page (sorted by clicks)</p>
            <div className="space-y-1.5" data-testid="eb-analytics-by-page">
              {pages.slice(0, 8).map(([page, stats]) => (
                <div key={page} className="flex items-center justify-between text-xs gap-2">
                  <code className="text-[#1E1B4B] font-mono truncate flex-1">{page || "/"}</code>
                  <span className="text-[#475569] flex-shrink-0">
                    <span className="font-semibold text-[#1E1B4B]">{stats.impressions || 0}</span> imp
                    <span className="mx-1.5 text-gray-300">·</span>
                    <span className="font-semibold text-[#DC283C]">{stats.clicks || 0}</span> clicks
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-xs text-[#94a3b8] italic">No traffic captured yet — the strip and badge fire impressions on render and clicks on tap. Data starts populating immediately.</p>
        )}

        {recent_bookings.length > 0 && (
          <div className="border-t border-gray-100 pt-3">
            <p className="text-[11px] uppercase tracking-wider text-[#475569] font-semibold mb-2">Recent early-bird bookings</p>
            <div className="space-y-1 text-xs" data-testid="eb-analytics-bookings">
              {recent_bookings.slice().reverse().slice(0, 5).map(b => (
                <div key={b.id} className="flex items-center justify-between gap-2">
                  <span className="text-[#1E1B4B] font-mono">{b.id}</span>
                  <span className="text-[#475569]">{b.package} · ₹{((b.payment_amount || 0) / 100).toLocaleString("en-IN")}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${b.payment_status === "paid" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                    {b.payment_status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EarlyBirdAnalyticsCard;
